import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Trash2, ChevronLeft, ChevronRight, Image, Calendar, Copy, ExternalLink, RefreshCw } from 'lucide-react';
import { API_URL } from './App';

export default function GeneratedImagesPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchImages(1);
  }, []);

  const fetchImages = async (page) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login to view your images');
        setLoading(false);
        return;
      }
      
      console.log(`Fetching images for page ${page}...`);
      
      const response = await fetch(`${API_URL}/user/images?page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      console.log('Images API response:', data);
      
      if (data.success) {
        setImages(data.images || []);
        setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
        
        // Auto-select first image if available and no image selected
        if (data.images && data.images.length > 0 && !selectedImage) {
          setSelectedImage(data.images[0]);
        }
      } else {
        setError(data.error || 'Failed to fetch images');
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/user/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setImages(images.filter(img => img.id !== imageId));
        if (selectedImage?.id === imageId) {
          setSelectedImage(images.length > 1 ? images[0] : null);
        }
      } else {
        alert(data.error || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const downloadImage = async (imageUrl, prompt) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bibleai-${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Failed to download image:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const copyImageUrl = (imageUrl) => {
    navigator.clipboard.writeText(imageUrl);
    alert('Image URL copied to clipboard!');
  };

  const handleRefresh = () => {
    fetchImages(pagination.page);
  };

  if (loading && images.length === 0) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <Image className="w-6 h-6 mr-2 text-purple-400" />
              Generated Images
            </h1>
            <p className="text-gray-400">
              {pagination.total} {pagination.total === 1 ? 'image' : 'images'} generated
            </p>
          </div>
          
          <button
            onClick={handleRefresh}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 flex items-center">
              <span className="mr-2">⚠️</span>
              {error}
            </p>
          </div>
        )}

        {images.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700">
            <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No images yet</h3>
            <p className="text-gray-400 mb-6">Start generating images in the Studio</p>
            <a
              href="/generate"
              className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
            >
              Go to Studio
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Thumbnail Grid */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-4">
                <h3 className="text-sm font-medium mb-3">All Images</h3>
                <div className="space-y-2 max-h-[calc(100vh-12rem)] overflow-y-auto pr-2">
                  {images.map((image) => (
                    <motion.div
                      key={image.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedImage?.id === image.id
                          ? 'bg-purple-600/20 border border-purple-500/50'
                          : 'bg-gray-800 hover:bg-gray-700'
                      }`}
                      onClick={() => setSelectedImage(image)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-700 rounded overflow-hidden flex-shrink-0">
                          <img
                            src={image.image_url}
                            alt={image.prompt}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/48?text=Error';
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs truncate">{image.prompt || 'No prompt'}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {image.created_at ? new Date(image.created_at).toLocaleDateString() : 'Unknown date'}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                    <button
                      onClick={() => fetchImages(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => fetchImages(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Image Viewer */}
            <div className="lg:col-span-3">
              {selectedImage ? (
                <motion.div
                  key={selectedImage.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gray-800/30 rounded-lg border border-gray-700 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 mr-4">
                      <h2 className="text-lg font-semibold mb-1 line-clamp-2">
                        {selectedImage.prompt || 'Untitled'}
                      </h2>
                      <div className="flex items-center text-sm text-gray-400">
                        <Calendar className="w-4 h-4 mr-1" />
                        {selectedImage.created_at ? new Date(selectedImage.created_at).toLocaleString() : 'Unknown date'}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => downloadImage(selectedImage.image_url, selectedImage.prompt || 'image')}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => copyImageUrl(selectedImage.image_url)}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(selectedImage.image_url, '_blank')}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteImage(selectedImage.id)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="relative bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
                    <img
                      src={selectedImage.image_url}
                      alt={selectedImage.prompt || 'Generated image'}
                      className="w-full h-auto object-contain max-h-[60vh]"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/512?text=Image+Error';
                      }}
                    />
                  </div>

                  <div className="mt-4 text-sm text-gray-400">
                    <span className="font-medium text-white">Model:</span> {selectedImage.model || 'Flux1schnell'}
                  </div>
                </motion.div>
              ) : (
                <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-12 text-center">
                  <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Select an image</h3>
                  <p className="text-gray-400">Choose an image from the gallery to view</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}